import {
  Alert,
  AppShellContent,
  Stack,
  Text,
  Tile,
  Title,
} from "@assetwatch/ui";
import { useMatches } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { pageAccess } from "../lib/pageAccess";
import { findPage } from "../pages/registry";
import { AppNotFound } from "./AppNotFound";
import { useFeatureFlags } from "./LaunchDarkly/useFeatureFlags";

const PAGE_ROUTE_PREFIX = "/_app/";

/**
 * The page folder the current route belongs to. Page routes are
 * `/_app/<id>`, where `<id>` is the folder name, so no prop is needed — which
 * is what lets a copied `route.tsx` work unchanged.
 */
function useCurrentPageId(): string | undefined {
  return useMatches({
    select: (matches) =>
      matches
        .map((match) => match.routeId as string)
        .find(
          (routeId) =>
            routeId.startsWith(PAGE_ROUTE_PREFIX) &&
            routeId.length > PAGE_ROUTE_PREFIX.length,
        )
        ?.slice(PAGE_ROUTE_PREFIX.length),
  });
}

/**
 * The chrome every page route renders inside: its own flag gate, the page
 * header, and the local preview warning.
 *
 * Gating here as well as in the sidebar is deliberate — a page that's off
 * renders not-found, so it isn't revealed by guessing its URL.
 */
export function PageShell({ children }: { children: ReactNode }) {
  const pageId = useCurrentPageId();
  const page = pageId ? findPage(pageId) : undefined;
  const flags = useFeatureFlags();
  const access = page ? pageAccess(page, flags) : "hidden";

  if (!page || access === "hidden") {
    return <AppNotFound />;
  }

  return (
    <AppShellContent
      pageHeader={
        <Tile padding="comfortable" radius="md" tone="informative">
          <Stack gap={4}>
            <Title size="lg">{page.title}</Title>
            <Text>{page.description}</Text>
          </Stack>
        </Tile>
      }
    >
      {access === "preview" ? (
        <Stack gap={24}>
          <Alert
            subtext={`Shown because this page is in VITE_ML_FRONTEND_PREVIEW_PAGES. Deployed builds ignore that, so before this ships the "mlFrontend-${page.id}" flag must exist in LaunchDarkly, default false, targeted to its testers.`}
            title="Local preview: LaunchDarkly flag is off"
            tone="warning"
          />
          {children}
        </Stack>
      ) : (
        children
      )}
    </AppShellContent>
  );
}
